import React from "react";
import { NavLink, Navigate, Outlet, Link } from "react-router-dom";
import { LayoutDashboard, Package, ShoppingBag, Ticket, Settings, FileText, HelpCircle, Inbox, LogOut, ExternalLink, Star, Quote, LayoutGrid } from "lucide-react";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { LOGO } from "@/data/images";

const LINKS = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/orders", label: "Orders", icon: ShoppingBag },
  { to: "/admin/products", label: "Products & Prices", icon: Package },
  { to: "/admin/categories", label: "Categories", icon: LayoutGrid },
  { to: "/admin/reviews", label: "Reviews", icon: Star },
  { to: "/admin/testimonials", label: "Testimonials", icon: Quote },
  { to: "/admin/coupons", label: "Offers & Coupons", icon: Ticket },
  { to: "/admin/settings", label: "Site Settings", icon: Settings },
  { to: "/admin/pages", label: "Legal Pages", icon: FileText },
  { to: "/admin/faqs", label: "FAQs", icon: HelpCircle },
  { to: "/admin/inbox", label: "Inbox", icon: Inbox },
];

export default function AdminLayout() {
  const { user, logout } = useAdminAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center text-sm text-mutedgreen">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  return (
    <div data-testid="admin-layout" className="min-h-screen bg-cream flex flex-col lg:flex-row">
      <aside className="lg:w-64 bg-forestdeep text-cream flex flex-col lg:min-h-screen">
        <div className="px-5 py-5 flex items-center justify-between lg:block">
          <div className="inline-block bg-cream rounded-xl px-3 py-1.5"><img src={LOGO} alt="Nutribest" className="h-9 w-auto mix-blend-multiply" /></div>
          <p className="hidden lg:block mt-3 text-[10px] uppercase tracking-[0.2em] text-cream/50">Admin Panel</p>
          <button data-testid="admin-logout-mobile" onClick={logout} className="lg:hidden p-2 text-cream/70"><LogOut size={18} /></button>
        </div>
        <nav className="flex lg:flex-col gap-1 px-3 pb-3 overflow-x-auto lg:flex-1">
          {LINKS.map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end} data-testid={`admin-nav-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className={({ isActive }) => `flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-colors ${isActive ? "bg-cream text-forest" : "text-cream/70 hover:bg-cream/10 hover:text-cream"}`}>
              <Icon size={15} /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden lg:block px-5 py-5 border-t border-cream/10 space-y-3">
          <p className="text-xs text-cream/60 truncate">{user.email}</p>
          <Link to="/" target="_blank" className="flex items-center gap-2 text-xs text-cream/70 hover:text-goldlight"><ExternalLink size={13} /> View website</Link>
          <button data-testid="admin-logout" onClick={logout} className="flex items-center gap-2 text-xs text-cream/70 hover:text-goldlight"><LogOut size={13} /> Sign out</button>
        </div>
      </aside>
      <main className="flex-1 px-5 sm:px-8 lg:px-12 py-8 lg:py-10 max-w-6xl w-full">
        <Outlet />
      </main>
    </div>
  );
}
